import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/site/PageHeader";
import { ServicesGrid } from "@/components/site/ServicesGrid";
import { EstimateForm } from "@/components/site/EstimateForm";
import { CTASection } from "@/components/site/CTASection";
import residentialAsset from "@/assets/residential-interior.jpg.asset.json";
const residentialImg = residentialAsset.url;

export const Route = createFileRoute("/residential")({
  head: () => ({
    meta: [
      { title: "Residential Painting in Wisconsin — Michael's Painting" },
      { name: "description", content: "Premium interior, exterior, and cabinet painting for Wisconsin homeowners. Clean process, sharp lines, durable finishes." },
      { property: "og:title", content: "Residential Painting in Wisconsin" },
      { property: "og:description", content: "Premium interior, exterior, and cabinet painting for Wisconsin homeowners." },
      { property: "og:url", content: "https://michaelspaintingwi.com/residential" },
      { property: "og:image", content: `https://michaelspaintingwi.com${residentialAsset.url}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://michaelspaintingwi.com${residentialAsset.url}` },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/residential" }],
  }),
  component: ResidentialPage,
});

const FEATURES = [
  { t: "Interior Painting", d: "Walls, ceilings, trim, doors — surgical lines and a flawless finish." },
  { t: "Exterior Painting", d: "Siding, soffits, fascia, doors. Built for Wisconsin freeze-thaw cycles." },
  { t: "Cabinet Refinishing", d: "Sprayed enamels for a factory-grade finish on existing cabinetry." },
  { t: "Drywall Repair & Prep", d: "Patches, skim coats, and surface prep that disappear under the topcoat." },
  { t: "Decks, Fences & Staining", d: "Premium translucent and solid stains for outdoor wood." },
  { t: "Color Consultation", d: "Expert palette guidance with samples in your actual light." },
];

function ResidentialPage() {
  return (
    <>
      <PageHeader eyebrow="Residential Painting" title="Painting that protects what matters most." subtitle="From a single accent wall to a full exterior restoration, our residential crews deliver a finish you'll feel proud of every time you come home." />
      <section className="py-24 lg:py-32 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.2fr,1fr] gap-16 items-start">
          <div>
            <h2 className="font-serif text-4xl text-brand-navy mb-6 text-balance">A finish your family can live with for years.</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">We treat your home as a sanctuary. Floors are covered. Furniture is moved with care. Daily clean-up is non-negotiable. And every coat is held to the standard we'd expect in our own homes.</p>
            <img src={residentialImg} alt="Residential interior painting in Wisconsin" loading="lazy" className="rounded-md w-full mt-6" />
          </div>
          <div className="lg:sticky lg:top-28"><EstimateForm /></div>
        </div>
      </section>
      <section className="py-24 bg-brand-ivory-soft">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-serif text-4xl text-brand-navy mb-12 text-center">Residential capabilities</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={f.t} className="p-8 bg-brand-ivory rounded-md border border-zinc-200/60">
                <span className="font-serif italic text-brand-brass text-2xl">0{i + 1}</span>
                <h3 className="font-serif text-xl text-brand-navy mt-3 mb-2">{f.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <ServicesGrid title="Browse all services" subtitle="Every service we offer for Wisconsin homes." />
      <CTASection title="Plan your next home painting project." subtitle="Tell us about your home — we'll provide a detailed proposal with a fixed price." />
    </>
  );
}
